import React, { useCallback, useEffect, useRef, useState } from "react";
import { Spinner, Dropdown, DropdownToggle, DropdownMenu, DropdownItem } from "reactstrap";
import { useService } from "../../../../../base/hooks/useService";
import UsersService from "../../../../../services/UsersService";
import UserTagService from "../../../../../services/UserTagService";
import Icon from "../../../../../base/components/Icon";
import UserSummaryCard from "./UserSummaryCard";

const renderMsg = (type, msg) => (
  <div
    className={
      type === "info"
        ? "alert alert-info text-center mb-3 d-flex align-items-center justify-content-start"
        : "alert alert-danger text-center mb-3 d-flex align-items-center justify-content-start"
    }
    style={{ fontSize: 15 }}
  >
    <Icon
      icon={type === "info" ? "infoCircleOutline" : "isCriticalResult"}
      className="me-2"
      style={{ fontSize: 20 }}
    />
    {msg}
  </div>
);

const renderQuestionnaireConflictDropdown = (
  questionnaireResolution,
  setQuestionnaireResolution,
  dropdownOpen,
  setDropdownOpen
) => {
  const showPlaceholder = !questionnaireResolution;
  return (
    <div className="my-3 w-50">
      <label className="fw-bold mb-1">Resolve questionnaire conflict:</label>
      <Dropdown
        isOpen={dropdownOpen}
        toggle={() => setDropdownOpen(!dropdownOpen)}
        className="d-inline-block filter-dropdown cursor-pointer result-filter-responsible dropdown"
      >
        <DropdownToggle
          className={`filter-toggle flex-grow-1 w-100 text-start${dropdownOpen ? " with-border" : ""}`}
          tag="section"
        >
          <span className={`ms-2 me-1 pointer-events-none user-select-none${showPlaceholder ? " text-secondary" : ""}`}>
            {showPlaceholder
              ? "Select an option"
              : questionnaireResolution === "keepSource"
              ? "Keep Source Users answers"
              : questionnaireResolution === "keepTarget"
              ? "Keep Target Users answers"
              : "Combine, keeping latest by timestamp"}
          </span>
          <i className={`mdi mdi-chevron-down pointer-events-none user-select-none${dropdownOpen ? " mdi-rotate-180" : ""}`} />
        </DropdownToggle>
        <DropdownMenu className="filter-menu pb-1 px-1 w-100">
          <DropdownItem className="ps-2" onClick={() => setQuestionnaireResolution("keepSource")}>
            Keep Source Users answers
          </DropdownItem>
          <DropdownItem className="ps-2" onClick={() => setQuestionnaireResolution("keepTarget")}>
            Keep Target Users answers
          </DropdownItem>
          <DropdownItem className="ps-2" onClick={() => setQuestionnaireResolution("latestSubmission")}>
            Combine, keeping latest by timestamp
          </DropdownItem>
        </DropdownMenu>
      </Dropdown>
    </div>
  );
};

const renderShopifyConflictDropdown = (
  shopifyPreference,
  setShopifyPreference,
  dropdownOpen,
  setDropdownOpen
) => {
  const showPlaceholder = !shopifyPreference;
  return (
    <div className="my-3 w-50">
      <label className="fw-bold mb-1">Resolve Shopify account conflict:</label>
      <Dropdown
        isOpen={dropdownOpen}
        toggle={() => setDropdownOpen(!dropdownOpen)}
        className="d-inline-block filter-dropdown cursor-pointer result-filter-responsible dropdown"
      >
        <DropdownToggle
          className={`filter-toggle flex-grow-1 w-100 text-start${dropdownOpen ? " with-border" : ""}`}
          tag="section"
        >
          <span className={`ms-2 me-1 pointer-events-none user-select-none${showPlaceholder ? " text-secondary" : ""}`}>
            {showPlaceholder
              ? "Select an option"
              : shopifyPreference === "keepSource"
              ? "Keep Source Shopify account"
              : shopifyPreference === "keepTarget"
              ? "Keep Target Shopify account"
              : ""}
          </span>
          <i className={`mdi mdi-chevron-down pointer-events-none user-select-none${dropdownOpen ? " mdi-rotate-180" : ""}`} />
        </DropdownToggle>
        <DropdownMenu className="filter-menu pb-1 px-1 w-100">
          <DropdownItem className="ps-2" onClick={() => setShopifyPreference("keepSource")}>
            Keep Source Shopify account
          </DropdownItem>
          <DropdownItem className="ps-2" onClick={() => setShopifyPreference("keepTarget")}>
            Keep Target Shopify account
          </DropdownItem>
        </DropdownMenu>
      </Dropdown>
    </div>
  );
};

const userBlock = (user, labs, title, recommendations, questionnaires, testKits) => (
  <UserSummaryCard
    user={user}
    labs={labs}
    recommendations={recommendations}
    questionnaires={questionnaires}
    testKits={testKits}
    title={title}
  />
);

const StepReview = ({
  selectedUserInfo,
  selectedTargetUser,
  setShouldSwapSourceTarget,
  setReviewError,
  reviewError,
  questionnaireResolution,
  setQuestionnaireResolution,
  setQuestionnaireConflict,
  shopifyPreference,
  setShopifyPreference,
  setShopifyConflict,
  sourceTestKits,
  setSourceTestKits,
  sourceLabs,
  setSourceLabs,
  targetTestKits,
  setTargetTestKits,
  targetLabs,
  setTargetLabs,
  sourceRecommendations,
  setSourceRecommendations,
  targetRecommendations,
  setTargetRecommendations,
  sourceQuestionnaires,
  setSourceQuestionnaires,
  targetQuestionnaires,
  setTargetQuestionnaires,
}) => {
  const usersService = useService(UsersService);
  const userTagService = useService(UserTagService);

  const [sourceUser, setSourceUser] = useState(null);
  const [targetUser, setTargetUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [questionnaireErrorMsg, setQuestionnaireErrorMsg] = useState("");
  const [shopifyErrorMsg, setShopifyErrorMsg] = useState("");
  const [swapInfoMsg, setSwapInfoMsg] = useState("");
  const [shopifySwapInfoMsg, setShopifySwapInfoMsg] = useState("");
  const [questionnaireDropdownOpen, setQuestionnaireDropdownOpen] = useState(false);
  const [shopifyDropdownOpen, setShopifyDropdownOpen] = useState(false);

  const initialSourceIdRef = useRef(selectedUserInfo?.id);
  const initialTargetIdRef = useRef(selectedTargetUser?.id);

  const initialDataRef = useRef({
    srcUser: null,
    tgtUser: null,
    srcLabs: [],
    tgtLabs: [],
    srcLabsWithResults: [],
    tgtLabsWithResults: [],
    srcRecs: [],
    tgtRecs: [],
    srcTags: [],
    tgtTags: [],
  });

  const setSourceAndTargetUsers = useCallback(
    (
      currSourceUser,
      currTargetUser,
      currSourceTestKits,
      currTargetTestKits,
      currSourceLabs,
      currTargetLabs,
      currSourceRecommendations,
      currTargetRecommendations,
      currSourceQuestionnaires,
      currTargetQuestionnaires
    ) => {
      setSourceUser(currSourceUser);
      setTargetUser(currTargetUser);

      setSourceTestKits(currSourceTestKits || []);
      setTargetTestKits(currTargetTestKits || []);

      setSourceLabs(currSourceLabs || []);
      setTargetLabs(currTargetLabs || []);

      setSourceRecommendations(currSourceRecommendations || []);
      setTargetRecommendations(currTargetRecommendations || []);

      setSourceQuestionnaires(currSourceQuestionnaires || []);
      setTargetQuestionnaires(currTargetQuestionnaires || []);
    },
    [
      setSourceUser,
      setTargetUser,
      setSourceTestKits,
      setTargetTestKits,
      setSourceLabs,
      setTargetLabs,
      setSourceRecommendations,
      setTargetRecommendations,
      setSourceQuestionnaires,
      setTargetQuestionnaires,
    ]
  );

  useEffect(() => {
    let cancelled = false;
    async function fetchData() {
      setLoading(true);
      try {
        const [
          srcUser,
          tgtUser,
          srcLabs,
          tgtLabs,
          srcRecs,
          tgtRecs,
          srcTags,
          tgtTags,
        ] = await Promise.all([
          usersService.getUserById(selectedUserInfo?.id),
          usersService.getUserById(selectedTargetUser?.id),
          usersService.getUserLabsResults(selectedUserInfo?.id),
          usersService.getUserLabsResults(selectedTargetUser?.id),
          usersService.getUsersRecommendations(selectedUserInfo?.id),
          usersService.getUsersRecommendations(selectedTargetUser?.id),
          userTagService.getUserTags(selectedUserInfo?.id),
          userTagService.getUserTags(selectedTargetUser?.id),
        ]);
        if (!cancelled) {
          initialDataRef.current = {
            srcUser,
            tgtUser,
            srcLabs: srcLabs?.data || [],
            tgtLabs: tgtLabs?.data || [],
            srcLabsWithResults: (srcLabs?.data || []).filter(lab => lab?.resultFile != null),
            tgtLabsWithResults: (tgtLabs?.data || []).filter(lab => lab?.resultFile != null),
            srcRecs: Array.isArray(srcRecs?.data) ? srcRecs.data : [],
            tgtRecs: Array.isArray(tgtRecs?.data) ? tgtRecs.data : [],
            srcTags: Array.isArray(srcTags?.data) ? srcTags.data : [],
            tgtTags: Array.isArray(tgtTags?.data) ? tgtTags.data : [],
          };

          // Switch logic: if source has results/recommendations and target only questionnaires
          const srcHasResultsOrRecs =
            initialDataRef.current.srcLabs.length > 0 ||
            initialDataRef.current.srcRecs.length > 0;
          const tgtHasOnlyQuestionnaires =
            initialDataRef.current.tgtLabs.length === 0 &&
            initialDataRef.current.tgtRecs.length === 0 &&
            initialDataRef.current.tgtTags.length > 0;

          if (srcHasResultsOrRecs && tgtHasOnlyQuestionnaires) {
            setSwapInfoMsg(
              "Source user has results/recommendations while target user only has questionnaires. Swapping source and target for merge."
            );
            setShouldSwapSourceTarget(true);
            setSourceAndTargetUsers(
              initialDataRef.current.tgtUser,
              initialDataRef.current.srcUser,
              initialDataRef.current.tgtLabs,
              initialDataRef.current.srcLabs,
              initialDataRef.current.tgtLabsWithResults,
              initialDataRef.current.srcLabsWithResults,
              initialDataRef.current.tgtRecs,
              initialDataRef.current.srcRecs,
              initialDataRef.current.tgtTags,
              initialDataRef.current.srcTags
            );
          } else {
            setSwapInfoMsg("");
            setShouldSwapSourceTarget(false);
            setSourceAndTargetUsers(
              initialDataRef.current.srcUser,
              initialDataRef.current.tgtUser,
              initialDataRef.current.srcLabs,
              initialDataRef.current.tgtLabs,
              initialDataRef.current.srcLabsWithResults,
              initialDataRef.current.tgtLabsWithResults,
              initialDataRef.current.srcRecs,
              initialDataRef.current.tgtRecs,
              initialDataRef.current.srcTags,
              initialDataRef.current.tgtTags
            );
          }

          const srcShopifyId = initialDataRef.current.srcUser.additionalField?.shopifyCustomerId;
          const tgtShopifyId = initialDataRef.current.tgtUser.additionalField?.shopifyCustomerId;
          const srcIsShopify = srcShopifyId !== null && srcShopifyId !== undefined;
          const tgtIsShopify = tgtShopifyId !== null && tgtShopifyId !== undefined;

          if (srcIsShopify && tgtIsShopify) {
            setShopifyErrorMsg("Both source and target users are Shopify accounts. Please choose which Shopify account to keep.");
            setShopifyConflict(true);
          } else if (tgtIsShopify && !srcIsShopify) {
            setShopifySwapInfoMsg("Target user is a Shopify account but source user is not. Swapping source and target for merge.");
            setShopifyConflict(false);
            // Swap users so source becomes the Shopify account
            setShouldSwapSourceTarget(true);
            setSourceAndTargetUsers(
              initialDataRef.current.tgtUser,
              initialDataRef.current.srcUser,
              initialDataRef.current.tgtTestKits,
              initialDataRef.current.srcTestKits,
              initialDataRef.current.tgtLabs,
              initialDataRef.current.srcLabs,
              initialDataRef.current.tgtRecs,
              initialDataRef.current.srcRecs,
              initialDataRef.current.tgtTags,
              initialDataRef.current.srcTags
            );
          } else {
            setShopifyErrorMsg("");
            setShopifySwapInfoMsg("");
            setShopifyConflict(false);
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    if (selectedUserInfo?.id && selectedTargetUser?.id) {
      fetchData();
    }
    return () => {
      cancelled = true;
    };
  }, [
    selectedUserInfo?.id,
    selectedTargetUser?.id,
    usersService,
    userTagService,
    setSourceLabs,
    setTargetLabs,
    setSourceRecommendations,
    setTargetRecommendations,
    setSourceQuestionnaires,
    setTargetQuestionnaires,
    setSourceAndTargetUsers,
    setSwapInfoMsg
  ]);

  useEffect(() => {
    if (!(sourceUser && targetUser)) return;

    const srcFirstName = sourceUser?.firstName ?? null;
    const tgtFirstName = targetUser?.firstName ?? null;
    const srcSex = sourceUser?.additionalField?.sex ?? null;
    const tgtSex = targetUser?.additionalField?.sex ?? null;
    const srcDob = sourceUser?.additionalField?.dateOfBirth ?? null;
    const tgtDob = targetUser?.additionalField?.dateOfBirth ?? null;
    
    const mismatches = [];
    const nullValues = [];
    const errorText = "does not match between source and target user.";
    
    if (srcFirstName == null || tgtFirstName == null) {
      nullValues.push("FirstName");
    }
    if (srcSex == null || tgtSex == null) {
      nullValues.push("gender");
    }
    if (srcDob == null || tgtDob == null) {
      nullValues.push("DOB");
    }
    
    if (srcSex && tgtSex && srcSex !== tgtSex) {
      mismatches.push("Gender");
    }
    if (srcDob && tgtDob && srcDob !== tgtDob) {
      mismatches.push("DOB");
    }

    const errors = [];

    if (nullValues.length > 0) {
      const nullErrorText = "has null values in source or target user.";
      if (nullValues.length === 1) {
        errors.push(`${nullValues[0]} ${nullErrorText}`);
      } else if (nullValues.length === 2) {
        errors.push(`${nullValues[0]} and ${nullValues[1]} ${nullErrorText}`);
      } else {
        const lastNull = nullValues.pop();
        errors.push(`${nullValues.join(", ")} and ${lastNull} ${nullErrorText}`);
      }
    }

    if (mismatches.length === 1) {
      errors.push(`${mismatches[0]} ${errorText}`);
    } else if (mismatches.length > 1) {
      const last = mismatches.pop();
      errors.push(`${mismatches.join(", ")} and ${last} ${errorText}`);
    }

    if (Array.isArray(sourceTestKits) && Array.isArray(targetTestKits)) {
      const sourceCodes = new Set(
        sourceTestKits.map((l) => l.sampleCode).filter(Boolean)
      );
      const targetCodes = new Set(
        targetTestKits.map((l) => l.sampleCode).filter(Boolean)
      );
      const overlapping = [...sourceCodes].filter((code) =>
        targetCodes.has(code)
      );
      if (overlapping.length > 0) {
        errors.push(`Overlapping Sample IDs: ${overlapping.join(", ")}`);
      }

      const activeStatuses = [2, 5, 6, 7, 9]; // [inProgress, canceled, processedByInitialLab, analysisInitiated, reprocessingRequired]
      const srcHasActiveKit = sourceTestKits.some(kit => activeStatuses.includes(kit.status));
      const tgtHasActiveKit = targetTestKits.some(kit => activeStatuses.includes(kit.status));
      const activeKitUsers = [];
      const activeKitUsersErrorText = "have Active Kits";

      if (srcHasActiveKit) {
        activeKitUsers.push("Source");
      }
      if (tgtHasActiveKit) {
        activeKitUsers.push("Target");
      }
      if (activeKitUsers.length === 1) {
        errors.push(`${activeKitUsers[0]} user ${activeKitUsersErrorText}`);
      } else if (activeKitUsers.length > 1) {
        errors.push(`${activeKitUsers[0]} and ${activeKitUsers[1]} users ${activeKitUsersErrorText}`);
      }
    }

    let combinedError = "";
    if (errors.length === 1) {
      combinedError = errors[0];
    } else if (errors.length > 1) {
      combinedError = errors;
    }
    setReviewError(combinedError);

    if (
      Array.isArray(sourceQuestionnaires) &&
      sourceQuestionnaires.length > 0 &&
      Array.isArray(targetQuestionnaires) &&
      targetQuestionnaires.length > 0
    ) {
      const normalize = (arr) =>
        arr
          .map((q) => ({ key: q.key, value: q.value }))
          .sort((a, b) => a.key.localeCompare(b.key));
      const srcQ = JSON.stringify(normalize(sourceQuestionnaires));
      const tgtQ = JSON.stringify(normalize(targetQuestionnaires));
      if (srcQ !== tgtQ) {
        setQuestionnaireErrorMsg("Conflicting questionnaire answers between source and target user.");
        setQuestionnaireConflict(true);
      } else {
        setQuestionnaireConflict(false);
      }
    } else {
      setQuestionnaireConflict(false);
    }
  }, [
    sourceUser,
    targetUser,
    sourceTestKits,
    sourceLabs,
    targetTestKits,
    targetLabs,
    sourceQuestionnaires,
    targetQuestionnaires,
    setReviewError,
  ]);

  useEffect(() => {
    if (!sourceUser || !targetUser) return;
    if (!shopifyPreference) return;

    const srcShopifyId = sourceUser?.additionalField?.shopifyCustomerId;
    const tgtShopifyId = targetUser?.additionalField?.shopifyCustomerId;
    const srcIsShopify = srcShopifyId !== null && srcShopifyId !== undefined;
    const tgtIsShopify = tgtShopifyId !== null && tgtShopifyId !== undefined;

    if (!srcIsShopify || !tgtIsShopify) return;

    const originalSourceId = initialSourceIdRef.current;
    const originalTargetId = initialTargetIdRef.current;
    const {
      srcUser,
      tgtUser,
      srcLabs,
      tgtLabs,
      srcLabsWithResults,
      tgtLabsWithResults,
      srcRecs,
      tgtRecs,
      srcTags,
      tgtTags,
    } = initialDataRef.current;

    if (shopifyPreference === "keepSource") {
      if (targetUser?.id !== originalSourceId) {
        setShouldSwapSourceTarget(true);
        setSourceAndTargetUsers(
          tgtUser,
          srcUser,
          tgtLabs,
          srcLabs,
          tgtLabsWithResults,
          srcLabsWithResults,
          tgtRecs,
          srcRecs,
          tgtTags,
          srcTags
        );
      }
    } else if (shopifyPreference === "keepTarget") {
      if (targetUser?.id !== originalTargetId) {
        setShouldSwapSourceTarget(false);
        setSourceAndTargetUsers(
          srcUser,
          tgtUser,
          srcLabs,
          tgtLabs,
          srcLabsWithResults,
          tgtLabsWithResults,
          srcRecs,
          tgtRecs,
          srcTags,
          tgtTags
        );
      }
    }
  }, [
    shopifyPreference,
    sourceUser,
    targetUser,
    setSourceAndTargetUsers,
    setShouldSwapSourceTarget,
  ]);

  if (loading) {
    return (
      <div className="text-center py-4">
        <Spinner />
      </div>
    );
  }

  return (
    <div>
      {swapInfoMsg && renderMsg("info", swapInfoMsg)}
      {shopifySwapInfoMsg && renderMsg("info", shopifySwapInfoMsg)}
      {reviewError && renderMsg("error", reviewError)}
      {questionnaireErrorMsg && renderMsg("error", questionnaireErrorMsg)}
      {questionnaireErrorMsg &&
        renderQuestionnaireConflictDropdown(
          questionnaireResolution,
          setQuestionnaireResolution,
          questionnaireDropdownOpen,
          setQuestionnaireDropdownOpen
        )}
      {shopifyErrorMsg && renderMsg("error", shopifyErrorMsg)}
      {shopifyErrorMsg &&
        renderShopifyConflictDropdown(
          shopifyPreference,
          setShopifyPreference,
          shopifyDropdownOpen,
          setShopifyDropdownOpen
        )}
      <div className="d-flex gap-2 justify-content-center">
        {userBlock(
          sourceUser,
          sourceLabs,
          "Source User",
          sourceRecommendations,
          sourceQuestionnaires,
          sourceTestKits
        )}
        <div
          className="d-flex align-items-center justify-content-center"
          style={{ minWidth: 60 }}
        >
          <Icon icon="between" className="between-icon" />
        </div>
        {userBlock(
          targetUser,
          targetLabs,
          "Target User",
          targetRecommendations,
          targetQuestionnaires,
          targetTestKits
        )}
      </div>
    </div>
  );
};

export default StepReview;
